// Central analytics wrapper — web port of the mobile app's
// `tarbaa-pro-app/src/apis/analytics/index.js`. Single fan-out point.
//
// queueEvent(name, params):
//   1. Pushes the event (standard envelope merged in) onto an in-memory queue
//      batch-POSTed to /api/analytics/log-events (same endpoint as the app).
//   2. Mirrors it to Firebase Analytics with Firebase-safe params.
//
// Differences from the app, all forced by the platform:
//   - The ingest endpoint is JWT-only, so events queue while logged out and
//     flush on the first authenticated flush (capped, oldest dropped).
//   - No SecureStore play counter → the envelope carries `platform: "web"`
//     instead of games_played_total.
//   - Session boundaries come from the Page Visibility API, not AppState.
import { BASE_URL, instance } from "../api";
import { getToken } from "../api/storage";
import { EVENTS } from "./events";
import {
  logFirebaseEvent,
  logFirebaseScreenView,
  setFirebaseUserId,
  setFirebaseUserProperties,
} from "./firebase";

let eventQueue = [];
let flushInFlight = null;

const BATCH_SIZE = 20;
const FLUSH_INTERVAL = 300; // seconds (timed flush, same as the app)
// Logged-out visitors can't reach the ingest endpoint — keep a bounded backlog
// so a long anonymous session can't grow the queue without limit.
const MAX_QUEUE = 300;

const IS_DEV = process.env.NODE_ENV === "development";

// ── Client properties (sent as userProperties, like the app's deviceData) ──
const clientData = {
  platform: "web",
  device_os: navigator.platform || "unknown",
  user_agent: (navigator.userAgent || "unknown").slice(0, 100),
  app_version: process.env.REACT_APP_VERSION || "web",
  screen_width: window.screen?.width || 0,
  screen_height: window.screen?.height || 0,
  language: navigator.language || "unknown",
};

// ── Standard envelope state ───────────────────────────────────────────────
let currentTier = "free";
let sessionId = generateSessionId();
let sessionStartedAt = Date.now();

function generateSessionId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getEnvelope() {
  return {
    tier: currentTier,
    session_id: sessionId,
    platform: "web",
  };
}

// ── Firebase helpers ──────────────────────────────────────────────────────
// Firebase limits: event name ≤40 chars snake_case, ≤25 params, param name
// ≤40 chars, string values ≤100 chars, no nested objects/arrays.
function sanitizeForFirebase(params) {
  const clean = {};
  let count = 0;
  for (const [key, value] of Object.entries(params || {})) {
    if (count >= 25) break;
    if (value === null || value === undefined) continue;
    const safeKey = String(key).slice(0, 40);
    if (typeof value === "string") {
      clean[safeKey] = value.slice(0, 100);
    } else if (typeof value === "number" || typeof value === "boolean") {
      clean[safeKey] = value;
    } else if (Array.isArray(value)) {
      clean[safeKey] = value.join(",").slice(0, 100);
    } else {
      continue; // drop nested objects — Firebase rejects them
    }
    count++;
  }
  return clean;
}

// Sync user properties to both Firebase and the in-memory envelope.
// Called from AnalyticsBridge whenever the subscription tier resolves.
export function setAnalyticsUserProperties({ tier, paid_version } = {}) {
  if (tier) currentTier = tier;
  if (IS_DEV) return;
  const props = { platform: "web" };
  if (tier !== undefined) props.tier = String(tier);
  if (paid_version !== undefined) props.paid_version = String(paid_version);
  setFirebaseUserProperties(props);
}

export function setAnalyticsUserId(userId) {
  if (IS_DEV) return;
  setFirebaseUserId(userId);
}

// ── Sessions ──────────────────────────────────────────────────────────────
export function startSession(source = "cold_start") {
  sessionId = generateSessionId();
  sessionStartedAt = Date.now();
  queueEvent(EVENTS.SESSION_STARTED, { source });
}

export function endSession() {
  queueEvent(EVENTS.SESSION_ENDED, { duration_ms: Date.now() - sessionStartedAt });
  // The tab may be going away — use the keepalive path so the batch survives.
  flushEvents({ keepalive: true });
}

// ── Screen tracking (called from AnalyticsBridge on route change) ─────────
export function trackScreenView(screenName, previousScreen) {
  queueEvent(EVENTS.SCREEN_VIEWED, {
    screen_name: screenName,
    previous_screen: previousScreen || "none",
  });
  if (IS_DEV) return;
  logFirebaseScreenView(screenName);
}

// ── Core queue ────────────────────────────────────────────────────────────
export async function queueEvent(eventName, eventParams = {}) {
  const params = { ...getEnvelope(), ...eventParams };

  if (IS_DEV) {
    // Don't send events in development — log for debugging instead.
    console.log(`[analytics] ${eventName}`, params);
    return;
  }

  eventQueue.push({
    eventName,
    eventParams: params,
    eventTimestampMicros: Date.now() * 1000, // ms -> µs
    userProperties: clientData,
  });
  if (eventQueue.length > MAX_QUEUE) {
    eventQueue = eventQueue.slice(-MAX_QUEUE);
  }

  logFirebaseEvent(eventName, sanitizeForFirebase(params));

  if (eventQueue.length >= BATCH_SIZE) {
    await flushEvents();
  }
}

// Flush the queue to the backend. Concurrent calls await the in-flight POST
// instead of dropping the batch (same guard as the app).
// `keepalive` uses fetch(keepalive) so an unload/hide flush isn't cancelled.
export async function flushEvents({ keepalive = false } = {}) {
  if (IS_DEV) return;

  // Ingest is JWT-only: hold everything until the visitor is authenticated.
  const token = getToken();
  if (!token) return;

  if (eventQueue.length === 0) {
    // Timed flush with an empty queue → heartbeat keep-alive (matches the app;
    // the server uses it to know the session is still open).
    await queueEvent(EVENTS.SESSION_HEARTBEAT);
  }

  if (flushInFlight) {
    await flushInFlight;
    if (eventQueue.length === 0) return;
  }

  const batch = eventQueue;
  eventQueue = [];

  const send = keepalive
    ? fetch(`${BASE_URL}/api/analytics/log-events`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "X-Client-Platform": "web",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ events: batch }),
      })
    : instance.post("/api/analytics/log-events", { events: batch });

  flushInFlight = Promise.resolve(send)
    .catch((error) => {
      // Re-queue the failed batch ahead of anything queued meanwhile.
      eventQueue = [...batch, ...eventQueue].slice(-MAX_QUEUE);
      console.error(
        "❌ Failed to send batch events:",
        error?.response?.data || error?.message,
      );
    })
    .finally(() => {
      flushInFlight = null;
    });

  await flushInFlight;
}

// Timed flush: if the queue doesn't reach BATCH_SIZE quickly, it still flushes
// after FLUSH_INTERVAL seconds.
if (!IS_DEV) {
  setInterval(() => {
    flushEvents();
  }, FLUSH_INTERVAL * 1000);
}

export { EVENTS };
